import get from "lodash.get";

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@reactive-resume/ui";
import { t } from "@lingui/macro";
import { DialogName } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";
import { T } from "@lingui/react/dist/shared/react.e5f95de8";
import type { SectionWithItem } from "@reactive-resume/schema";
import { Copy, DotsThreeVertical, Eye, EyeSlash, TrashSimple, X } from "@phosphor-icons/react";
import AiAssistantIcon from "@/client/pages/builder/Icons/AiAssistantIcon";

type SectionListItemOptionProps = {
  id: DialogName;
  index: number;
  itemId: string;
  onDuplicate?: () => void;
  onItemRemoved?: (itemId: string) => void;
};

export const SectionListItemOptions = ({ id, index, itemId, onDuplicate, onItemRemoved }: SectionListItemOptionProps) => {
  const setValue = useResumeStore((state) => state.setValue);
  const section = useResumeStore((state) => {
    return get(state.resume.data.sections, id);
  }) as SectionWithItem<T> | null;

  // handles onclick of "visibility" action
  const onToggleVisibility = () => {
    const visible = get(section, `items[${index}].visible`, true);
    setValue(`sections.${id}.items[${index}].visible`, !visible);
  };

  // handles onclick of "customizable" action
  const onToggleCustomization = () => {
    const canAICustomize = get(section, `items[${index}].canAICustomize`, false);
    setValue(`sections.${id}.items[${index}].canAICustomize`, !canAICustomize);
  };

  // handles onclick of "remove" action
  const onRemove = () => {
    if (!section) return;

    const updatedItems = [...section.items];
    updatedItems.splice(index, 1);

    setValue(`sections.${id}.items`, updatedItems);

    // Notify parent that this item was removed so it can clear expanded state if needed
    onItemRemoved && onItemRemoved(itemId);
  };

  // handles onclick of "copy" action
  const onCopy = () => {
    onDuplicate && onDuplicate();
  };

  const canShowCustomization =
    ["basics", "summary", "experience", "skills"].includes(id) || id.startsWith("custom");

  return (
    <DropdownMenu>
      {/* Trigger */}
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6 p-0">
          <DotsThreeVertical weight="bold" />
        </Button>
      </DropdownMenuTrigger>
      {/* Content */}
      <DropdownMenuContent className="w-48">
        <DropdownMenuGroup>
          {/* Copy Item */}
          <DropdownMenuItem onClick={onCopy}>
            <Copy />
            <span className="ml-2">{t`Copy`}</span>
          </DropdownMenuItem>

          {/* Visible/hide item */}
          <DropdownMenuItem onClick={onToggleVisibility}>
            {section?.visible ? <Eye /> : <EyeSlash />}
            <span className="ml-2">{section?.items[index]?.visible ? t`Hide` : t`Show`}</span>
          </DropdownMenuItem>

          {/* AI Customizable check item */}
          {canShowCustomization && (
            <DropdownMenuItem onClick={onToggleCustomization}>
              {section?.items[index]?.canAICustomize ? (
                <AiAssistantIcon size={18} className="grayscale" />
              ) : (
                <AiAssistantIcon size={18} />
              )}
              <span className="ml-2">
                {section?.items[index]?.canAICustomize ? "Disable Tailor" : "Enable Tailor"}
              </span>
            </DropdownMenuItem>
          )}

          {/* Delete item */}
          <DropdownMenuItem className="text-error" onClick={onRemove}>
            <TrashSimple />
            <span className="ml-2">{t`Remove`}</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
